import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EYEBROW, MONO } from './register';

/* ---------------------------------------------------------------------------
   Shared primitives for the public pages, after the Yunlen Business marketing
   site: one pooled IntersectionObserver per option set, a block reveal, the
   clipped display line, an on-screen ticker, and the bracket card.
   --------------------------------------------------------------------------- */

let reducedQuery;
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  reducedQuery = reducedQuery || window.matchMedia('(prefers-reduced-motion: reduce)');
  return reducedQuery.matches;
}

/* ---------- Pooled observer ----------
   Every reveal and ticker on a page shares one IntersectionObserver per option
   set, instead of one observer per element. */

const pools = new Map();

export function observe(el, callback, { threshold = 0.1, rootMargin = '0px 0px -8% 0px', once = true } = {}) {
  if (typeof IntersectionObserver === 'undefined') {
    callback({ isIntersecting: true });
    return () => {};
  }
  const key = `${threshold}|${rootMargin}`;
  let pool = pools.get(key);
  if (!pool) {
    const listeners = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const record = listeners.get(entry.target);
          if (!record) continue;
          record.callback(entry);
          if (record.once && entry.isIntersecting) {
            listeners.delete(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold, rootMargin }
    );
    pool = { io, listeners };
    pools.set(key, pool);
  }
  pool.listeners.set(el, { callback, once });
  pool.io.observe(el);
  return () => {
    pool.listeners.delete(el);
    pool.io.unobserve(el);
  };
}

/* ---------- Block reveal ----------
   Fades a block up the first time it enters the viewport. `stagger` delays
   each direct child by its index instead. Classes are toggled on the DOM node,
   not through state, so a reveal never re-renders its subtree. */

export function Reveal({ as: Tag = 'div', className = '', delay = 0, y = 24, stagger, style, children, ...rest }) {
  const ref = useRef(null);
  const staggered = stagger !== undefined;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !staggered) return;
    Array.from(el.children).forEach((child, i) => child.style.setProperty('--i', String(i)));
  }, [staggered, children]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return undefined;
    // Already on screen at mount: leave it alone rather than blink it out and back.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) return undefined;
    el.classList.add('is-armed');
    let settle = 0;
    const show = () => {
      el.classList.remove('is-armed');
      el.classList.add('is-shown');
      // Drop the reveal transition once it has run, so a child's own hover
      // transition is not stretched by the stagger delay afterwards.
      settle = window.setTimeout(() => el.classList.remove('is-shown'), 2600);
    };
    const failSafe = window.setTimeout(show, 4000);
    const stop = observe(el, (entry) => {
      if (!entry.isIntersecting) return;
      window.clearTimeout(failSafe);
      show();
    });
    return () => {
      window.clearTimeout(failSafe);
      window.clearTimeout(settle);
      stop();
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`${staggered ? 'yl-reveal--stagger' : 'yl-reveal'} ${className}`.trim()}
      style={{
        '--reveal-delay': `${delay}s`,
        '--reveal-y': `${y}px`,
        '--reveal-stagger': `${stagger || 0}s`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ---------- Display lines ----------
   A headline arrives a line at a time, each clipped and rising on a spring.
   The heading carries the full sentence as its accessible name. */

export function SplitLines({ lines, as: Tag = 'span', className = '', delay = 0, accentLast = false }) {
  return (
    <Tag className={className} style={{ '--split-delay': `${delay}s` }}>
      {lines.map((line, i) => (
        <span key={line} className="yl-split-line" style={{ '--i': i }} aria-hidden="true">
          <span className={`yl-split-inner${accentLast && i === lines.length - 1 ? ' text-[#10b981]' : ''}`}>{line}</span>
        </span>
      ))}
    </Tag>
  );
}

/* ---------- Ticker ----------
   A counter that advances only while its element is on screen, so no looping
   visual runs in a section nobody is looking at. */

export function useTicker(count, every, ref) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || count < 2 || prefersReducedMotion()) return undefined;
    let timer = 0;
    const stop = observe(
      el,
      (entry) => {
        window.clearInterval(timer);
        if (entry.isIntersecting) timer = window.setInterval(() => setI((v) => (v + 1) % count), every);
      },
      { threshold: 0.2, rootMargin: '0px', once: false }
    );
    return () => {
      window.clearInterval(timer);
      stop();
    };
  }, [count, every, ref]);
  return i;
}

/* ---------- Eyebrow with a live dot ---------- */

export function LiveEyebrow({ children, className = '' }) {
  return (
    <p className={`${EYEBROW} flex items-center gap-3 text-[#565656] ${className}`}>
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="yl-ping absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10b981] opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#10b981]" />
      </span>
      {children}
    </p>
  );
}

/* ---------- Bracket card ----------
   A hairline rounded border with the middle of every side masked out, so only
   the corners remain. */

export function BracketCard({ className = '', children, tone = 'bg-[#ffffff]' }) {
  return (
    <div className={`relative rounded-[1.75rem] border border-[#c8c8c8] ${className}`}>
      <span aria-hidden="true" className={`absolute -top-px left-1/2 h-0.5 w-[70%] -translate-x-1/2 ${tone}`} />
      <span aria-hidden="true" className={`absolute -bottom-px left-1/2 h-0.5 w-[70%] -translate-x-1/2 ${tone}`} />
      <span aria-hidden="true" className={`absolute -left-px top-1/2 h-[73%] w-0.5 -translate-y-1/2 ${tone}`} />
      <span aria-hidden="true" className={`absolute -right-px top-1/2 h-[73%] w-0.5 -translate-y-1/2 ${tone}`} />
      {children}
    </div>
  );
}

/** Four L-shaped ticks outside a card, turning emerald when the card is the active step. */
export function Corners({ flash }) {
  const c = `absolute h-5 w-5 transition-colors duration-300 ${flash ? 'border-[#10b981]' : 'border-[#d6d6d6]'}`;
  return (
    <>
      <span aria-hidden="true" className={`${c} -left-3 -top-3 rounded-tl border-l-2 border-t-2`} />
      <span aria-hidden="true" className={`${c} -right-3 -top-3 rounded-tr border-r-2 border-t-2`} />
      <span aria-hidden="true" className={`${c} -bottom-3 -left-3 rounded-bl border-b-2 border-l-2`} />
      <span aria-hidden="true" className={`${c} -bottom-3 -right-3 rounded-br border-b-2 border-r-2`} />
    </>
  );
}

/* ---------- Currency discs ----------
   Overlapping currency marks dealt in after the hero headline: image flags
   and token logos, never flag emoji, which Windows does not draw. */

/* Marks borrowed from Yunlen Business: circle flags (country-flag-icons, MIT)
   for fiat, token logos (public/rails) for stablecoins. */
export const CURRENCY_MARKS = {
  USD: '/flags/US.svg',
  EUR: '/flags/EU.svg',
  GBP: '/flags/GB.svg',
  CAD: '/flags/CA.svg',
  USDC: '/rails/usdc.svg',
  USDT: '/rails/usdt.svg',
};

const DISC_TONES = {
  USD: 'bg-[#171717] text-[#ffffff]',
  EUR: 'bg-[#10b981] text-[#ffffff]',
  USDC: 'bg-[#e5edf4] text-[#171717]',
  USDT: 'bg-[#baecb7] text-[#0f5f3f]',
  USDB: 'bg-[#ececec] text-[#171717]',
};

export function CurrencyDiscs({ codes, size = 44, enter = false, className = '' }) {
  return (
    <span className={`inline-flex items-center -space-x-2.5 ${className}`} aria-hidden="true">
      {codes.map((code, i) => (
        <span
          key={code}
          className={`${MONO} ${enter ? 'yl-deal ' : ''}grid place-items-center rounded-full text-[0.625rem] font-medium tracking-[0.02em] ring-[3px] ring-[#ffffff] ${DISC_TONES[code] || DISC_TONES.USDB}`}
          style={{ width: size, height: size, '--i': i }}
        >
          {CURRENCY_MARKS[code] ? (
            <img src={CURRENCY_MARKS[code]} alt="" width={size} height={size} className="h-full w-full rounded-full object-cover" />
          ) : (
            code
          )}
        </span>
      ))}
    </span>
  );
}

/* ---------- Deferred third-party script ----------
   Loads a tag after the page is idle, so it never competes with the hero. */

export function useIdleScript(src, inline) {
  useEffect(() => {
    if (!src || document.querySelector(`script[src="${src}"]`)) return undefined;
    const run = () => {
      const tag = document.createElement('script');
      tag.async = true;
      tag.src = src;
      document.head.appendChild(tag);
      if (inline) {
        const body = document.createElement('script');
        body.textContent = inline;
        document.head.appendChild(body);
      }
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(run, 1500);
    return () => window.clearTimeout(id);
  }, [src, inline]);
}

/* ---------- GSAP, on demand ----------
   GSAP and ScrollTrigger are fetched once, after first paint, by whichever
   component asks first. Nothing in the hero waits for them. */

let gsapPromise;
export function loadGsap() {
  if (!gsapPromise) {
    gsapPromise = Promise.all([import('gsap'), import('gsap/ScrollTrigger')])
      .then(([core, st]) => {
        const gsap = core.gsap || core.default;
        const ScrollTrigger = st.ScrollTrigger || st.default;
        gsap.registerPlugin(ScrollTrigger);
        return { gsap, ScrollTrigger };
      })
      .catch(() => ({ gsap: null, ScrollTrigger: null }));
  }
  return gsapPromise;
}

/* ---------- Scroll-lit statement ----------
   Every word starts dim and lights up in reading order as the paragraph
   scrolls through the viewport (ScrollTrigger scrub). GSAP is requested when
   the paragraph comes within 600px; if it cannot load, the words simply
   light up at once. */

export function ScrubText({ text, className = '', accent = [] }) {
  const ref = useRef(null);
  const words = text.split(' ');

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const spans = Array.from(el.querySelectorAll('[data-w]'));
    const lightAll = () => spans.forEach((s) => s.style.setProperty('opacity', '1'));
    if (prefersReducedMotion()) {
      lightAll();
      return undefined;
    }
    let live = true;
    let ctx;
    const stop = observe(
      el,
      (entry) => {
        if (!entry.isIntersecting) return;
        loadGsap().then(({ gsap }) => {
          if (!live) return;
          if (!gsap) {
            lightAll();
            return;
          }
          ctx = gsap.context(() => {
            gsap.to(spans, {
              opacity: 1,
              ease: 'none',
              stagger: 0.12,
              scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 48%', scrub: 0.6 },
            });
          }, el);
        });
      },
      { threshold: 0, rootMargin: '600px 0px 600px 0px' }
    );
    return () => {
      live = false;
      stop();
      if (ctx) ctx.revert();
    };
  }, [text]);

  return (
    <p ref={ref} className={className}>
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          data-w=""
          className={accent.includes(w.replace(/[.,]/g, '')) ? 'text-[#10b981]' : undefined}
          style={{ opacity: 0.14 }}
        >
          {w}
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </p>
  );
}
